import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useContext, useEffect, useState} from 'react';
import Skeleton, {SkeletonTheme} from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import {ApiResult, InsertResultsWithData} from '../../../framework.core/data/ApiResult';
import {getTbNameByJobName, JobNames} from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import HawbMilestoneView from './implementation/HawbMilestone';

import {useTypedFastStorage} from '../../../hooks/useFastStorage';

import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import {SQLField, SQLInfo} from '../../../framework.core/data/SQLInfo';
import {TrackableEntity} from '../../../fms.core/data/ChangeTrackable';
import {checkPropDirty, getSQLInfoList2} from '../../../fms.core/data/TrackingModule';
import {HblStatusViewModel} from "../../../fms.core/HblStatusViewModel";
import {stringsEqualIgnoreCase} from "../../../util";
import SecondaryButton from '../../../ui/SecondaryButton';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import JobListViewContext from '../../common/JobListViewContext';
import fastStorage from "../../../fastStorage";
import { BlEventLog } from '../../../fms.core/BlEventLog';
import { BlEventName } from '../../../framework.core/BlEventName';
import { getFmsGlobal } from '../../../globals';
import constants from '../../../constants';

function HawbMilestoneImpl({
  hawbId,
  storageKey,
  onClose,
  isView,
  statusStorageKey
}: {
  hawbId: number,
  storageKey: string,
  statusStorageKey: string,
  onClose: (s: HblStatusViewModel|null) => void,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);

  const [blStatus, setBlStatus, removeBlStatus, blStatusRef] = useTypedFastStorage<HblStatusViewModel>(statusStorageKey, null);
  const {setCleanState, /*isDirtyComparedTo,*/ dirtyCleanup} = useDirtyCheck(statusStorageKey, ['updated', 'updatedBy']);
  const jobListContext = useContext(JobListViewContext);

  useEffect(() => {
      initialize();
      return () => {
        removeBlStatus();
        dirtyCleanup();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  
  function RefreshListView(hawbId: number) {
    if(!blStatus) return;

    const editModelKey = 'ifms-list-data-/common/joblistview/aiHawb/airImport';
    const keyMatch = _.keys(fastStorage).find(x => x.startsWith(editModelKey));
    if (keyMatch) {
      const listData = _.values(fastStorage[keyMatch]);
      if (checkPropDirty(blStatus, 'anSentChk')) listData.filter(x => x.id === hawbId)[0].anSent = blStatusRef.current?.anSentChk;
      if (checkPropDirty(blStatus, 'entryDocChk')) listData.filter(x => x.id === hawbId)[0].atmeSent = !!blStatusRef.current?.entryDocChk;
      jobListContext.updateDataRow(listData.filter(x => x.id === hawbId)[0]);
    }
  }

  async function loadBlStatus() {
    const apiResult:ApiResult<HblStatusViewModel> = await appContext.api.getHblStatusViewModel(JobNames.AIHbl, hawbId);

    if (!apiResult) {
      await showGlobalOkDialog('Cannot load data');
      onClose(null);
      return;
    }

    const loadedModelObj = new HblStatusViewModel(apiResult.result);
    setCleanState(loadedModelObj);

    setBlStatus(new HblStatusViewModel(_.cloneDeep(loadedModelObj)));

    if(blStatusRef.current !== null) {
      blStatusRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function initialize() {
    await loadBlStatus();
  }

  async function save() {
    if (!blStatus || !blStatusRef.current) {
      throw new Error('Invalid operation');
    }

    if(!blStatusRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      onClose(null);
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate(getTbNameByJobName(JobNames.AIHbl), hawbId, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }
    
    const sqlInfoList = getSQLInfoList2(blStatusRef.current as TrackableEntity, HblStatusViewModel.getFieldMappings(JobNames.AIHbl));
    sqlInfoList.push(...si);

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_aotherinfo")).forEach(x => {
      SQLInfo.setWhereFieldValueByFieldName(x, 'f_tbid', hawbId);
    });

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_aihmain")).forEach(x => {
      if(!SQLInfo.hasWhereFieldOfPropertyWithValue(x, 'f_id', hawbId)){
        x.whereFields.push(new SQLField({fieldName: 'f_id', value: hawbId}));
      }
    });

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveHblStatusViewModel(sqlInfoList);

    if (apiResult && apiResult.success) {
      await UpdateLogHawbMilestone();    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      RefreshListView(hawbId);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }

    onClose(blStatusRef.current);
  }

  async function UpdateLogHawbMilestone() {
    if (!blStatus) return;

    let cmile:BlEventLog[] = [];

    if (checkPropDirty(blStatus, 'anPrtDate')) {
      cmile.push(new BlEventLog(BlEventName.Fms7501, blStatus.anPrtDate))
    }
    if (checkPropDirty(blStatus, 'delSchedDate')) {
      cmile.push(new BlEventLog(BlEventName.Fms3461, blStatus.delSchedDate))
    }
    if (checkPropDirty(blStatus, 'delActDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelSched, blStatus.delActDate))
    }
    if (checkPropDirty(blStatus, 'emptyRetDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelAct, blStatus.emptyRetDate))
    }

    if (cmile.length > 0) {
      const tempData = cmile.sort((a, b) => new Date(a.eventAt ?? '').getTime() - new Date(b.eventAt ?? '').getTime());
      const lastEvents: BlEventLog = tempData[tempData.length - 1];
      lastEvents.suntekID = getFmsGlobal().AcmeID ?? '';
      lastEvents.jobName = JobNames.AIHbl;
      lastEvents.jobId = hawbId;
      lastEvents.blNo = blStatus.hblNo;
      //lastEvents.eventAtUtc
      lastEvents.u1Id = appContext.user?.id ?? '';
      const apiResult = await appContext.api.trackingLogHblMilestone(lastEvents);;
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      }
    }
  }

  return (
    <div>
      {!initialized &&
      <div style={{position: 'relative', top: '-25px'}}>
        <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
          <Skeleton count={1} height={2}/>
        </SkeletonTheme>
      </div>
      }
      {!!blStatus &&
      <div className='report-popup'>
        <HawbMilestoneInitializedImpl setBlStatus={setBlStatus} blStatus={blStatus} isView={isView}/>

        <div className='report-footer'>
          <SecondaryButton
            size="regular"
            style={{width: '90px'}}
            onClick={save}
            disabled={isView}>
            Update
          </SecondaryButton>
          <SecondaryButton
            size="regular"
            style={{width: '90px'}}
            onClick={() => {onClose(null)}}>
            Cancel
          </SecondaryButton>
        </div>
      </div>
      }
    </div>
  );
}

function HawbMilestoneInitializedImpl({
  isView,
  blStatus,
  setBlStatus
}: {
  isView: boolean,
  blStatus: HblStatusViewModel,
  setBlStatus: (x: Partial<HblStatusViewModel>) => void,
}) {
  return (
    <HawbMilestoneView blStatus={blStatus} onChange={setBlStatus} isView={isView} />
  );
}

const HawbMilestonePopup = ({
  hawbId,
  storageKey,
  onClose,
  isView,
  statusStorageKey
}: {
  hawbId: number,
  storageKey: string,
  statusStorageKey: string,
  onClose: (s: HblStatusViewModel|null) => void,
  isView: boolean,
}) => {
  return (
    <HawbMilestoneImpl
      hawbId={hawbId}
      key={storageKey}
      storageKey={storageKey}
      statusStorageKey={statusStorageKey}
      onClose={onClose}
      isView={isView}/>
  );
};

HawbMilestonePopup.defaultProps = {};

HawbMilestonePopup.propTypes = {
  hawbId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(HawbMilestonePopup);
